import React, { useState, useMemo, useEffect } from 'react'
import { Modal, Button, Checkbox, Icon, Empty } from 'antd'
import './FinalVideoSelection.scss'
import { ImagePreview } from '../../../../../../components'

const isSelectableShot = shot =>
  !!shot?.vidSrc || (!!shot?.is_static && !!shot?.imageSrc)

const buildSelectedClips = (scenes, selectedShotIds, transitions = {}) => {
  const clips = []

  scenes.forEach(scene => {
    const validShots = scene.shots.filter(
      shot => isSelectableShot(shot) && selectedShotIds.has(shot.id)
    )

    validShots.forEach((shot, shotIndex) => {
      const shotTransitionKey = `${scene.id}-${shot.id}`
      const sceneStartKey = `${scene.id}-start`
      const sceneEndKey = `${scene.id}-end`
      const clipTransitions = []

      if (shotIndex === 0 && transitions[sceneStartKey]) {
        clipTransitions.push({
          ...transitions[sceneStartKey],
          label: 'Scene Start',
          sceneId: scene.id,
          position: 'start'
        })
      }

      if (transitions[shotTransitionKey]) {
        clipTransitions.push({
          ...transitions[shotTransitionKey],
          label: 'Shot Transition',
          isEndTransition: true,
          sceneId: scene.id,
          position: shot.id
        })
      }

      if (shotIndex === validShots.length - 1 && transitions[sceneEndKey]) {
        clipTransitions.push({
          ...transitions[sceneEndKey],
          label: 'Scene End',
          isEndTransition: true,
          sceneId: scene.id,
          position: 'end'
        })
      }

      clips.push({
        sceneId: scene.id,
        shotId: shot.id,
        sceneTitle: scene.title,
        shotTitle: shot.title,
        vidSrc: shot.vidSrc,
        imageSrc: shot.imageSrc,
        is_static: !!shot.is_static,
        video_duration: shot.video_duration,
        generated_video_duration: shot.generated_video_duration,
        transitions: clipTransitions.length > 0 ? clipTransitions : null,
        transition: clipTransitions[0] || null,
        audio_link: scene.audio_link
      })
    })
  })

  return clips
}

const FinalVideoSelection = ({
  visible,
  scenes,
  transitions = {},
  isAdvancedMode = false,
  finalVideoExists,
  isGenerating,
  onCancel,
  onConfirm,
  onAdvancedEditor
}) => {
  const defaultSelectedIds = useMemo(() => {
    const ids = new Set()
    scenes.forEach(scene => {
      scene.shots.forEach(shot => {
        if (isSelectableShot(shot)) ids.add(shot.id)
      })
    })
    return ids
  }, [scenes])

  const [selectedShotIds, setSelectedShotIds] = useState(defaultSelectedIds)

  useEffect(() => {
    if (visible) {
      setSelectedShotIds(new Set(defaultSelectedIds))
    }
  }, [visible, defaultSelectedIds])

  const toggleShot = shotId => {
    const newSelected = new Set(selectedShotIds)
    if (newSelected.has(shotId)) {
      newSelected.delete(shotId)
    } else {
      newSelected.add(shotId)
    }
    setSelectedShotIds(newSelected)
  }

  const toggleScene = (scene, checked) => {
    const newSelected = new Set(selectedShotIds)
    scene.shots.forEach(shot => {
      if (isSelectableShot(shot)) {
        if (checked) {
          newSelected.add(shot.id)
        } else {
          newSelected.delete(shot.id)
        }
      }
    })
    setSelectedShotIds(newSelected)
  }

  const getSelectedClips = () =>
    buildSelectedClips(scenes, selectedShotIds, transitions)

  const handleGenerate = () => {
    const selectedClips = getSelectedClips()
    if (selectedClips.length === 0) return

    Modal.confirm({
      title: finalVideoExists
        ? 'Overwrite Final Video'
        : 'Generate Final Video',
      content: finalVideoExists
        ? 'A final video already exists. Generating a new one will overwrite the existing video. Do you want to continue?'
        : 'Are you sure you want to generate the final video?',
      centered: true,
      okText: finalVideoExists ? 'Yes, Overwrite' : 'Yes, Generate',
      okType: finalVideoExists ? 'danger' : 'primary',
      cancelText: 'Cancel',
      onOk() {
        onConfirm(selectedClips)
      }
    })
  }

  const handleAdvancedEditor = () => {
    const selectedClips = getSelectedClips()
    if (selectedClips.length === 0 || !onAdvancedEditor) return

    onAdvancedEditor(selectedClips)
  }

  const canContinue = selectedShotIds.size > 0

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={null}
      width="90%"
      style={{ top: 20, maxWidth: '1160px' }}
      centered
      onCancel={onCancel}
      footer={null}
      bodyStyle={{ padding: 0, background: '#f6f7fb', borderRadius: '12px' }}
      destroyOnClose
      getContainer={false}
    >
      <div className="final-video-selection">
        <div className="final-video-selection__header">
          <h1 className="final-video-selection__title">
            <Icon type="video-camera" />
            Select Final Clips
          </h1>
          <div className="final-video-selection__meta">
            <span className="final-video-selection__meta-pill">
              {selectedShotIds.size} selected
            </span>
            <span className="final-video-selection__meta-copy">
              Keep only the clips you want in your final edit.
            </span>
          </div>
        </div>

        <div className="final-video-selection__content">
          {scenes.length === 0 ? (
            <Empty description="No generated shots available" />
          ) : (
            scenes.map((scene, sceneIndex) => {
              const validShots = scene.shots.filter(isSelectableShot)
              if (validShots.length === 0) return null

              const isAllSelected = validShots.every(s =>
                selectedShotIds.has(s.id)
              )
              const isSomeSelected =
                validShots.some(s => selectedShotIds.has(s.id)) &&
                !isAllSelected

              return (
                <div key={scene.id} className="final-video-selection__scene">
                  <div className="final-video-selection__scene-header">
                    <div className="final-video-selection__scene-title-wrap">
                      <div className="final-video-selection__scene-title">
                        {scene.title || `Scene ${sceneIndex + 1}`}
                      </div>
                      <span className="final-video-selection__scene-count">
                        {validShots.length} shots
                      </span>
                    </div>
                    <Checkbox
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={e => toggleScene(scene, e.target.checked)}
                    >
                      Select All
                    </Checkbox>
                  </div>

                  <div className="final-video-selection__shots">
                    {validShots.map((shot, shotIndex) => {
                      const isSelected = selectedShotIds.has(shot.id)

                      return (
                        <div
                          key={shot.id}
                          className={`final-video-selection__shot-row ${
                            isSelected
                              ? 'final-video-selection__shot-row--selected'
                              : ''
                          }`}
                          onClick={() => toggleShot(shot.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggleShot(shot.id)
                            }
                          }}
                        >
                          {shot.imageSrc ? (
                            <ImagePreview
                              src={shot.imageSrc}
                              alt="Shot preview"
                              className="final-video-selection__shot-thumb"
                            />
                          ) : (
                            <div className="final-video-selection__shot-thumb final-video-selection__shot-thumb--empty">
                              <Icon type="picture" />
                            </div>
                          )}

                          <div className="final-video-selection__shot-copy">
                            <span className="final-video-selection__shot-title">
                              {shot.title || `Shot ${shotIndex + 1}`}
                            </span>
                            <span className="final-video-selection__shot-meta">
                              {shot.is_static ? 'Static image' : 'Video clip'}
                            </span>
                          </div>

                          <Checkbox checked={isSelected} />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="final-video-selection__footer">
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
          <div className="final-video-selection__actions">
            {/* Keep the current editor path nearby for a future restore.
            <Button size="large" onClick={handleContinue} disabled={!canContinue}>
              Open Video Editor
              <Icon type="right" />
            </Button>
            */}
            {isAdvancedMode && (
              <Button
                size="large"
                icon="build"
                onClick={handleAdvancedEditor}
                disabled={!canContinue || isGenerating}
              >
                Open Advanced Editor
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              icon="play-circle"
              loading={isGenerating}
              onClick={handleGenerate}
              disabled={!canContinue}
            >
              Generate Video
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default FinalVideoSelection
